/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, memo, useState } from 'react';

import { jsx } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';

import { TR, type TRProps } from '../../ui/primitives/tr';
import { type RowSpacing } from '../charlie-table.types';

const styles = cssMap({
	bodyRow: {
		// minHeight: '45px', this causes issues with Safari; leaving it commented so folks don't revert
		position: 'relative',
		border: 'none',
		borderImageWidth: 0,
		borderSpacing: 0,
		display: 'flex',
		width: '100%',
		alignItems: 'center',
	},

	compactRow: {
		// minHeight: '36px',  this causes issues with Safari; leaving it commented so folks don't revert
	},
});

export interface RowProps extends TRProps {
	spacing?: RowSpacing;
}

/**
 * __Row__
 *
 * A table row.
 *
 * - [Examples](https://atlassian.design/components/table/examples)
 */

export const Row = memo(
	forwardRef<HTMLTableRowElement, RowProps>(
		({ children, testId, spacing, xcss, isSelected, ...props }, ref) => {
			const [isHovered, setIsHovered] = useState(false);

			const handleMouseEnter = (event: React.MouseEvent<HTMLTableRowElement>) => {
				setIsHovered(true);
				fg('charly-table-with-row-table-handler') && props.onMouseEnter?.(event);
			};

			const handleMouseLeave = (event: React.MouseEvent<HTMLTableRowElement>) => {
				setIsHovered(false);
				fg('charly-table-with-row-table-handler') && props.onMouseLeave?.(event);
			};

			return (
				<TR
					ref={ref}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					backgroundColor={isHovered ? 'elevation.surface.raised.hovered' : 'elevation.surface'}
					isSelected={isSelected}
					testId={testId}
					xcss={cx(styles.bodyRow, spacing === 'compact' && styles.compactRow, xcss)}
					{...props}
				>
					{children}
				</TR>
			);
		},
	),
);
